import { Modal, useModals } from 'almanac-ui/Modal'
import { Button } from 'blaze-ui/Button'
import { Copy, Facebook, Gift01, LinkedIn, Twitter } from 'blaze-ui/icons'

import Styles from './ReferralModalV2.module.scss'
import { useState } from 'react'
import { ReferralsClient } from 'api/clients/ReferralsClient'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import { PillsInput } from 'components/PillsInput/PillsInput'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { uniq } from 'lodash'
import { isEmail } from 'validator'
import { Plugin } from 'prosemirror-state'
import { Send2 } from 'blaze-ui/icons/16'
import { ButtonLink } from 'blaze-ui/Button/ButtonLink'
import { useReferralUrl } from './hooks/useReferralUrl'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { Text } from 'blaze-ui/Text/Text'
import { Heading } from 'blaze-ui/Heading/Heading'
import { pluralize } from 'utils'
import { FreeReferralRewards } from './FreeReferralRewards'
import { useRootStore } from 'stores/RootStore'
import { PaymentPlanKey } from 'api/clients/PaymentPlanClient'
import { CreditsReferralRewards } from './CreditsReferralRewards'
import { useIsSystemRedesign } from 'hooks/useIsSystemRedesign'

const IMAGE_URL = 'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/ReferralsV2_jypbg2'

export const ReferralModalV2 = ({
  onAction,
  initialEmails = [],
}: {
  onAction?: () => void
  initialEmails?: string[]
}) => {
  const isSystemRedesignEnabled = useIsSystemRedesign()
  const [emailAddressesList, setEmailAddressesList] = useState<string[]>(initialEmails)
  const { isMobile } = useResponsiveDesign()
  const { closeModal } = useModals()
  const [isSendingEmails, setIsSendingEmails] = useState(false)
  const workspace = useCurrentWorkspace()
  const isFreemiumReferrals = useFlipperFlag('freemium_referrals')
  const { currentSubscription } = useRootStore().tierUsageStore
  const isOnStarterOrGrowthPlan = [PaymentPlanKey.BlazeAutopilotStarter, PaymentPlanKey.BlazeAutopilotGrowth].includes(
    currentSubscription?.plan || PaymentPlanKey.Free,
  )

  const payingRewards = workspace?.referral_rewards?.paying || 0
  const freeRewards = workspace?.referral_rewards?.free || 0

  const isNewReferrals = workspace?.is_virtual_marketer && isFreemiumReferrals

  const { referralUrl } = useReferralUrl()

  const encodedReferralUrl = encodeURIComponent(referralUrl)
  const twitterPrefillText = encodeURIComponent(
    `Hey, I’ve been using @blazeaiapp to save 10+ hours a week, and it’s been a game-changer. Try it for free with my link: ${referralUrl}.`,
  )

  const copyUrlToClipboard = () => {
    navigator.clipboard.writeText(referralUrl)
    showToastMessage('Copied to clipboard!', 'success')
    onAction?.()
  }

  const handleInvite = () => {
    if (isSendingEmails) return
    setIsSendingEmails(true)
    ReferralsClient.sendReferralEmail(emailAddressesList, referralUrl).then((response) => {
      if (response.type === 'success') {
        showToastMessage(
          emailAddressesList.length > 1
            ? `${emailAddressesList.length} Invites sent successfully!`
            : ' Invite sent successfully!',
          'success',
        )
        setEmailAddressesList([])
        closeModal()
      } else {
        showErrorMessageToast('There was a problem sending your invites. Please try again or contact support.')
      }
      setIsSendingEmails(false)
    })
  }

  let title = "Don't Keep Blaze a Secret!"
  if (isOnStarterOrGrowthPlan) {
    title = 'Refer a user, earn 200 credits'
  }
  if (workspace?.is_ap_freemium) {
    title = 'Earn more posts every week!'
  }

  return (
    <Modal.Root onClose={closeModal} onPressOutside={closeModal} aria-label="Don’t Keep Blaze a Secret!">
      <Modal.Header
        variant="hero"
        title={title}
        heroImage={IMAGE_URL}
        heroImageAlt=""
        headingLevel={isMobile ? 2 : 1}
        onClose={() => closeModal()}
      />
      <Modal.Content className={Styles.content} withoutFooter={isSystemRedesignEnabled}>
        {workspace?.is_ap_freemium ||
          (!isOnStarterOrGrowthPlan && (
            <p className={Styles.paragraph}>
              {workspace?.is_ap_freemium
                ? 'Invite a friend to Blaze. Each successful referral permanently raises your weekly Autopilot post limit.'
                : 'Get a free month of Blaze when you refer friends to Blaze. Your referrals also get their first month free!'}
            </p>
          ))}

        {isNewReferrals && !workspace.is_ap_freemium && !workspace.is_campaigns && payingRewards > 0 && (
          <div className={Styles.payingMonthsEarned}>
            <Gift01 size={16} color="var(--magenta-50)" />
            <Heading level={4}>{payingRewards}</Heading>
            <Text>free {pluralize('month', payingRewards)} earned!</Text>
          </div>
        )}

        {isNewReferrals && workspace.is_ap_freemium && <FreeReferralRewards referrals={freeRewards} />}

        {isNewReferrals && !workspace.is_ap_freemium && workspace.is_campaigns && isOnStarterOrGrowthPlan && (
          <CreditsReferralRewards referrals={payingRewards} />
        )}

        <div className={Styles.inviteContainer}>
          <div className={Styles.inviteHeader}>Email an invite</div>
          <div className={Styles.inviteInputContainer}>
            <PillsInput
              autoFocus
              growOnFocus={false}
              placeholder="Enter multiple emails separated by comma"
              className={Styles.emailAddressesListContainer}
              splitOn={[',', ' ']}
              pills={emailAddressesList}
              onChange={(pills: string[] | undefined) => {
                const uniqPills = uniq(pills)
                setEmailAddressesList(uniqPills)
              }}
              validatePill={isEmail}
              plugins={{
                // No-op plugin so we don't load actual mentions plugin
                mentions: new Plugin({}),
              }}
              size="lg"
            />
            <Button
              frontIcon={Send2}
              onPress={handleInvite}
              isDisabled={!emailAddressesList.length || !referralUrl || isSendingEmails}
              fullWidth={isMobile}
              size="lg"
            >
              Send Invite
            </Button>
          </div>
        </div>

        <div className={Styles.shareContainer}>
          <div className={Styles.shareHeader}>Share your referral link</div>
          <div className={Styles.shareButtonsContainer}>
            <div className={Styles.urlContainer}>{referralUrl ? referralUrl : '...'}</div>
            <Button frontIcon={Copy} onPress={copyUrlToClipboard} isDisabled={!referralUrl} variant="secondary">
              Copy
            </Button>
          </div>

          <ResponsiveDesign.Mobile>
            <div className={Styles.socialHeader}>Share on social networks</div>
          </ResponsiveDesign.Mobile>

          <div className={Styles.socials}>
            <ButtonLink
              to={`https://www.facebook.com/sharer/sharer.php?u=${encodedReferralUrl}`}
              fullWidth
              frontIcon={Facebook}
              isDisabled={!referralUrl}
              onPress={() => onAction?.()}
              variant="secondary"
              rel="noopener noreferrer"
              target="_blank"
            >
              {!isMobile && 'Share on Facebook'}
            </ButtonLink>
            <ButtonLink
              to={`https://www.linkedin.com/sharing/share-offsite/?url=${encodedReferralUrl}`}
              fullWidth
              frontIcon={LinkedIn}
              isDisabled={!referralUrl}
              onPress={() => onAction?.()}
              variant="secondary"
              rel="noopener noreferrer"
              target="_blank"
            >
              {!isMobile && 'Post on LinkedIn'}
            </ButtonLink>
            <ButtonLink
              to={`https://twitter.com/intent/tweet?text=${twitterPrefillText}`}
              fullWidth
              frontIcon={Twitter}
              isDisabled={!referralUrl}
              onPress={() => onAction?.()}
              variant="secondary"
              rel="noopener noreferrer"
              target="_blank"
            >
              {!isMobile && 'Post on X/Twitter'}
            </ButtonLink>
          </div>
        </div>
      </Modal.Content>
    </Modal.Root>
  )
}
